import { httpClient } from "@coworker/http-client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { BasePermission } from "../types/BasePermission";
import { CountryPermission } from "../types/CountryPermission";
import { StorePermission } from "../types/StorePermission";
import { UserPermission } from "../types/UserPermission";

async function getAllPermissions(): Promise<BasePermission[]> {
  return await httpClient.core.get<BasePermission[]>(`/permissions`);
}

async function createPermission(data: {
  code: string;
  description: string;
}): Promise<BasePermission> {
  return await httpClient.core.post<BasePermission>(`/permissions`, {
    body: data,
  });
}

async function getAllCountryPermissions(): Promise<CountryPermission[]> {
  return await httpClient.core.get<CountryPermission[]>(
    `/permissions/country-permissions`
  );
}

async function createCountryPermission(data: {
  permission_id: string;
  country_id: string;
  value: boolean;
}): Promise<CountryPermission> {
  return await httpClient.core.post<CountryPermission>(
    `/permissions/country-permissions`,
    { body: data }
  );
}

async function updateCountryPermission(
  id: number,
  data: { value: boolean }
): Promise<CountryPermission> {
  return await httpClient.core.put<CountryPermission>(
    `/permissions/country-permissions/${id}`,
    { body: data }
  );
}

async function deleteCountryPermission(
  id: number
): Promise<{ success: boolean }> {
  return await httpClient.core.delete<{ success: boolean }>(
    `/permissions/country-permissions/${id}`
  );
}

async function getAllStorePermissions(): Promise<StorePermission[]> {
  return await httpClient.core.get<StorePermission[]>(
    `/permissions/store-permissions`
  );
}

async function createStorePermission(data: {
  permission_id: string;
  store_id: string;
  value: boolean;
}): Promise<StorePermission> {
  return await httpClient.core.post<StorePermission>(
    `/permissions/store-permissions`,
    { body: data }
  );
}

async function updateStorePermission(
  id: number,
  data: { value: boolean }
): Promise<StorePermission> {
  return await httpClient.core.put<StorePermission>(
    `/permissions/store-permissions/${id}`,
    { body: data }
  );
}

async function deleteStorePermission(
  id: number
): Promise<{ success: boolean }> {
  return await httpClient.core.delete<{ success: boolean }>(
    `/permissions/store-permissions/${id}`
  );
}

// ==================== React Query Hooks ====================

// Permission Hooks
export const useGetAllPermissions = () => {
  return useQuery({
    queryKey: ["permissions"],
    queryFn: getAllPermissions,
  });
};

export const useCreatePermission = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createPermission,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["permissions"],
        refetchType: "all",
      });
    },
  });
};

// Country Permission Hooks
export const useGetAllCountryPermissions = () => {
  return useQuery({
    queryKey: ["country-permissions"],
    queryFn: getAllCountryPermissions,
  });
};
export const useCreateCountryPermission = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createCountryPermission,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["country-permissions"],
        refetchType: "all",
      });
    },
  });
};

export const useUpdateCountryPermission = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: { value: boolean } }) =>
      updateCountryPermission(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["country-permissions"],
        refetchType: "all",
      });
    },
  });
};

export const useDeleteCountryPermission = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteCountryPermission,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["country-permissions"],
        refetchType: "all",
      });
    },
  });
};

// Store Permission Hooks
export const useGetAllStorePermissions = () => {
  return useQuery({
    queryKey: ["store-permissions"],
    queryFn: getAllStorePermissions,
  });
};

export const useCreateStorePermission = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createStorePermission,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["store-permissions"],
        refetchType: "all",
      });
    },
  });
};

export const useUpdateStorePermission = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: { value: boolean } }) =>
      updateStorePermission(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["store-permissions"],
        refetchType: "all",
      });
    },
  });
};

export const useDeleteStorePermission = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteStorePermission,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["store-permissions"],
        refetchType: "all",
      });
    },
  });
};

// ==================== Refresh User Scoped Permissions ====================

async function refreshUserScopedPermissions(): Promise<{ success: boolean }> {
  return await httpClient.core.post<{ success: boolean }>(
    `/permissions/user-scoped-permissions/refresh`,
    { body: {} }
  );
}

export const useRefreshUserScopedPermissions = () => {
  return useMutation({
    mutationFn: refreshUserScopedPermissions,
  });
};

// ==================== User Permissions ====================

async function getAllUserPermissions(): Promise<UserPermission[]> {
  return await httpClient.core.get<UserPermission[]>(
    `/permissions/user-permissions`
  );
}

async function createUserPermission(data: {
  permission_id: string;
  uid: string;
  value: boolean;
}): Promise<UserPermission> {
  return await httpClient.core.post<UserPermission>(
    `/permissions/user-permissions`,
    { body: data }
  );
}

async function updateUserPermission(
  id: number,
  data: { value: boolean }
): Promise<UserPermission> {
  return await httpClient.core.put<UserPermission>(
    `/permissions/user-permissions/${id}`,
    { body: data }
  );
}

async function deleteUserPermission(id: number): Promise<{ success: boolean }> {
  return await httpClient.core.delete<{ success: boolean }>(
    `/permissions/user-permissions/${id}`
  );
}

export const useGetAllUserPermissions = () => {
  return useQuery({
    queryKey: ["user-permissions"],
    queryFn: getAllUserPermissions,
  });
};

export const useCreateUserPermission = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createUserPermission,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["user-permissions"],
        refetchType: "all",
      });
    },
  });
};

export const useUpdateUserPermission = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: { value: boolean } }) =>
      updateUserPermission(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["user-permissions"],
        refetchType: "all",
      });
    },
  });
};

export const useDeleteUserPermission = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteUserPermission,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["user-permissions"],
        refetchType: "all",
      });
    },
  });
};
